/**
 * Project Detail (new rollup layer) — header + workstream strip.
 *
 * A "Project" here is the client build at a site (internally stored as `engagements`).
 * Workstream cards show dollar amounts READ from existing projects.sage_contract_amount
 * as a display value. No recomputation, no new aggregation, no writes.
 * Each card links to the existing /projects/:id page (the job-level WIP detail) for drill-down.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardActionArea, CardContent,
  Chip, CircularProgress, Alert, Stack, Divider, Breadcrumbs, Link as MuiLink,
  Button,
} from '@mui/material';
import {
  Apartment as ApartmentIcon, ArrowBack as ArrowBackIcon,
  Business as BusinessIcon, Home as HomeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getEngagement } from '../services/api';

const WORKSTREAM_COLORS = {
  AVIT: '#dce6f1',
  AV: '#dce6f1',
  Shades: '#e2efda',
  Lighting: '#fff2cc',
  'Lighting Control': '#fff2cc',
  'Lighting Fixtures': '#fce4d6',
  LT: '#fce4d6',
};

// Guess the workstream category from the project name
function categorize(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('shade')) return 'Shades';
  if (n.includes('ltrnsh')) return 'Shades';
  if (n.includes('light') && n.includes('fix')) return 'Lighting Fixtures';
  if (n.includes('lt fix')) return 'Lighting Fixtures';
  if (n.includes('ltrnlt')) return 'Lighting Control';
  if (n.includes('lighting control') || n.includes('ltrn')) return 'Lighting Control';
  if (n.includes('av') || n.includes('it')) return 'AVIT';
  return 'Other';
}

const fmt$ = (n) => (n === null || n === undefined || n === '') ? '—' :
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));

export default function EngagementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getEngagement(id);
        setData(res.data);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          setError('Project not found or Projects layer disabled on server.');
        } else {
          setError('Failed to load project.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="warning" sx={{ m: 3 }}>{error}</Alert>;
  if (!data) return null;

  const { engagement, workstreams } = data;
  const totalWorkstreams = workstreams.length;

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/engagements" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
          <ApartmentIcon fontSize="small" sx={{ mr: 0.5 }} /> Projects
        </MuiLink>
        <Typography color="text.primary">{engagement.display_name || engagement.client_name}</Typography>
      </Breadcrumbs>

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/engagements')} size="small" sx={{ mb: 2 }}>
        Back to projects
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="flex-start" spacing={3}>
          <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 2, p: 2 }}>
            <HomeIcon fontSize="large" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">{engagement.display_name || engagement.client_name}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {engagement.site_address}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">CLIENT</Typography>
                <Typography variant="body2" fontWeight={600}>{engagement.client_name} <Chip label={engagement.client_number} size="small" sx={{ ml: 1 }} /></Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">PRIMARY PM</Typography>
                <Typography variant="body2" fontWeight={600}>{engagement.primary_pm_name || '—'}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">SALES</Typography>
                <Typography variant="body2" fontWeight={600}>{engagement.primary_sales_name || '—'}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">WORKSTREAMS</Typography>
                <Typography variant="body2" fontWeight={600}>{totalWorkstreams}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">STATUS</Typography>
                <Box><Chip label={engagement.status} size="small" color={engagement.status === 'active' ? 'success' : 'default'} /></Box>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Workstream strip */}
      <Typography variant="h6" sx={{ mb: 1 }}>Workstreams</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Each card is a job number (a D-Tools proposal) under this project. Click to open the existing WIP detail page.
        Contract values are display-only reads from Sage WIP.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {workstreams.map((w) => {
          const cat = categorize(w.name);
          const bg = WORKSTREAM_COLORS[cat] || '#f5f5f5';
          return (
            <Grid item xs={12} sm={6} md={3} key={w.id}>
              <Card sx={{ borderTop: 4, borderTopColor: 'primary.main', bgcolor: bg }}>
                <CardActionArea onClick={() => navigate(`/projects/${w.id}`)}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Chip label={cat} size="small" color="primary" />
                      <Chip label={w.primary_job_number} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, minHeight: 40 }}>
                      {w.name}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={0.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Contract</Typography>
                        <Typography variant="body2" fontWeight={600}>{fmt$(w.sage_contract_amount)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Labor est.</Typography>
                        <Typography variant="body2" fontWeight={600}>{w.sage_labor_est ? `${Number(w.sage_labor_est).toFixed(0)} hrs` : '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">PM</Typography>
                        <Typography variant="body2">{w.pm_name || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Phase</Typography>
                        <Typography variant="body2">Phase {w.current_phase}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Placeholder for future unified tabs */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom><BusinessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />Unified Views (coming)</Typography>
        <Typography variant="body2" color="text.secondary">
          Future tabs — Proposals, Tasks, Documents, Dispatch, Timeline — will aggregate data across all workstreams at this engagement level.
          Today, use the workstream cards above to drill into each job individually.
        </Typography>
      </Paper>
    </Box>
  );
}
